import React, { useState, useEffect } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useLang } from '../../context/LangContext';
import TermsConsentSheet from '../TermsConsentSheet';
import { getTerms } from '../../lib/api/user/userApi';

const FooterContainer = styled.footer`
  width: 100%;
  background-color: #f3f3f3;
  padding: 40px 20px;
  margin-top: 60px;
  
  /* 모바일에서는 숨김 */
  @media (max-width: 768px) {
    display: none;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FooterSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 40px;
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 2;
  max-width: 600px;
  align-items: flex-start;
`;

const RightSection = styled.div`
  display: flex;
  gap: 40px;
  flex: 0 0 auto;
  justify-content: flex-end;
  align-items: flex-start;
`;

const PolicyRow = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;
`;

const Logo = styled.img`
  height: 40px;
  width: auto;
`;

const CompanyRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: flex-start;
`;

const BusinessRow = styled.div`
  display: flex;
  gap: 40px;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: nowrap;
`;

const FooterLink = styled.button`
  font-size: 14px;
  font-weight: 500;
  color: #666;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: #333;
  }
`;

const FooterText = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #666;
  margin: 0;
  line-height: 1.5;
  text-align: left;
`;

// 국제화 텍스트 함수
const getFooterTexts = (language: 'ko' | 'en') => {
  if (language === 'en') {
    return {
      companyName: 'Company Name: Lindenbaum Academy Co., Ltd.',
      representative: 'CEO: Won Sung Jun',
      businessNumber: 'Business Registration Number: 738-87-00576',
      email: 'Email: bidspace@thebidspace.com',
      address: 'Address: 2nd Floor, 319 Gobong-ro, Ilsanseo-gu, Goyang-si, Gyeonggi-do',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy'
    };
  }
  
  return {
    companyName: '상호: (주)린덴바움아카데미',
    representative: '대표자: 원성준',
    businessNumber: '사업자등록번호: 738-87-00576',
    email: '이메일: bidspace@thebidspace.com',
    address: '주소: 경기도 고양시 일산서구 고봉로 319, 2층',
    terms: '이용약관',
    privacy: '개인정보처리방침'
  };
};

const Footer: React.FC = () => {
  const { lang } = useLang();
  const texts = getFooterTexts(lang);
  
  // 약관 시트 관련 상태
  const [isTermsSheetOpen, setIsTermsSheetOpen] = useState(false);
  const [currentTermsSection, setCurrentTermsSection] = useState('terms');
  const [termsSection, setTermsSection] = useState<any[]>([]);
  const [isLoadingTerms, setIsLoadingTerms] = useState(false);

  // 언어 변경 감지하여 약관 데이터 초기화
  useEffect(() => {
    // 언어가 변경되면 기존 약관 데이터를 초기화
    // 바텀시트가 열린 상태에서는 언어 변경이 불가능하므로 단순히 초기화만 함
    setTermsSection([]);
  }, [lang]);

  // 약관 데이터 불러오기 - 필요할 때만 호출
  const fetchTerms = async () => {
    try {
      setIsLoadingTerms(true);
      const termLang = lang === 'ko' ? 'KOR' : 'ENG';
      const response = await getTerms({ termLang });
      
      if (response.data.statusCode === 200 && response.data.data) {
        const termsArray = response.data.data as any[];
        
        // 언어에 따라 인덱스 매핑
        let privacyIndex, serviceIndex;
        if (lang === 'ko') {
          // KOR: 1(privacy), 3(service)
          privacyIndex = termsArray.find((term: any) => term.index === 3);
          serviceIndex = termsArray.find((term: any) => term.index === 1);
        } else {
          // ENG: 2(privacy), 4(service)
          privacyIndex = termsArray.find((term: any) => term.index === 4);
          serviceIndex = termsArray.find((term: any) => term.index === 2);
        }

        const formattedTerms = [
          {
            key: 'terms',
            title: lang === 'ko' ? '서비스\n이용약관' : 'Terms of\nService',
            content: serviceIndex?.content || ''
          },
          {
            key: 'privacy',
            title: lang === 'ko' ? '개인정보\n취급방침' : 'Privacy\nPolicy',
            content: privacyIndex?.content || ''
          }
        ];

        setTermsSection(formattedTerms);
      }
    } catch (error) {
      devError('약관 조회 실패:', error);
      toast.error(lang === 'ko' ? '약관을 불러오는데 실패했습니다.' : 'Failed to load terms.');
    } finally {
      setIsLoadingTerms(false);
    }
  };

  // 약관 시트 핸들러
  const handleViewTerms = () => {
    setCurrentTermsSection('terms');
    setIsTermsSheetOpen(true);
    // 시트가 열린 후 데이터 로드 (언어 변경으로 데이터가 초기화된 경우도 포함)
    if (termsSection.length === 0 && !isLoadingTerms) {
      fetchTerms();
    }
  };

  const handleViewPrivacyPolicy = () => {
    setCurrentTermsSection('privacy');
    setIsTermsSheetOpen(true);
    // 시트가 열린 후 데이터 로드 (언어 변경으로 데이터가 초기화된 경우도 포함)
    if (termsSection.length === 0 && !isLoadingTerms) {
      fetchTerms();
    }
  };

  const handleTermsSheetClose = () => {
    setIsTermsSheetOpen(false);
  };

  const handleTermsSectionClick = (sectionKey: string) => {
    setCurrentTermsSection(sectionKey);
  };
  return (
    <FooterContainer>
      <FooterContent>
        <FooterSection>
          <LeftSection>
            <Logo src="/logo.svg" alt="BidSpace" />
            <CompanyRow>
              <FooterText>{texts.companyName}</FooterText>
              <FooterText>{texts.representative}</FooterText>
            </CompanyRow>
            <BusinessRow>
              <FooterText>{texts.businessNumber}</FooterText>
              <FooterText>{texts.email}</FooterText>
            </BusinessRow>
            <FooterText>
              {texts.address}
            </FooterText>
          </LeftSection>
          
          <RightSection>
            <PolicyRow>
              <FooterLink onClick={handleViewTerms}>{texts.terms}</FooterLink>
              <FooterLink onClick={handleViewPrivacyPolicy}>{texts.privacy}</FooterLink>
            </PolicyRow>
          </RightSection>
        </FooterSection>
        
        {/* 주석 처리된 부분 제거 */}
      </FooterContent>
      
      {/* 약관 시트 */}
      {isTermsSheetOpen && (
        <TermsConsentSheet
          sections={termsSection}
          viewedSection={currentTermsSection}
          onClose={handleTermsSheetClose}
          onSectionClick={handleTermsSectionClick}
          isLoading={isLoadingTerms}
        />
      )}
    </FooterContainer>
  );
};

export default Footer;
